import { createContext, PropsWithChildren, useState } from 'react';

import { isReconnectSubscriptionError } from '@/graphql/errors';
import { useGuardedContext } from '@/hooks/useGuardedContext';
import { useRefetchOnAppResume } from '@/hooks/useRefetchOnAppResume';
import { useReportError } from '@/hooks/useReportError';

import {
  useValidationQuestionsAnsweredSubscription,
  ValidationQuestionsAnsweredSubscription,
} from './graphql/ValidationQuestionsAnswered.generated';

interface ValidationQuestionsAnswers {
  householdId: string | null;
  answeredAt: Date | null;
  ready: boolean;
  unavailable: boolean;
}

const ValidationQuestionsAnswersContext = createContext<
  ValidationQuestionsAnswers | undefined
>(undefined);

/** Own the answer-completion subscription for the loaded household. */
export function ValidationQuestionsAnswersProvider({
  householdId,
  children,
}: PropsWithChildren<{ householdId: string | null }>) {
  const { reportError } = useReportError();
  const [household, setHousehold] =
    useState<ValidationQuestionsAnsweredSubscription['household']>();
  const [failedHouseholdId, setFailedHouseholdId] = useState<string | null>(
    null
  );
  const ready = !!householdId && household?.id === householdId;
  const answeredAt = ready
    ? (household.validationQuestionsAnsweredAt ?? null)
    : null;
  const { restart } = useValidationQuestionsAnsweredSubscription({
    variables: { householdId: householdId ?? '' },
    skip: !householdId || !!answeredAt,
    onData: ({ data }) => {
      if (data.data?.household.id !== householdId) return;
      setHousehold(data.data.household);
      setFailedHouseholdId(null);
    },
    onError: (error) => {
      if (isReconnectSubscriptionError(error)) {
        restart();
        return;
      }
      setFailedHouseholdId(householdId);
      reportError('Failed to read validation question answer status', error, {
        householdId,
      });
    },
  });
  useRefetchOnAppResume(restart, {
    label: `HouseholdValidationAnswers:${householdId}`,
    enabled: !!householdId && !answeredAt,
  });

  return (
    <ValidationQuestionsAnswersContext.Provider
      value={{
        householdId,
        answeredAt,
        ready,
        unavailable: !!householdId && failedHouseholdId === householdId,
      }}
    >
      {children}
    </ValidationQuestionsAnswersContext.Provider>
  );
}

/** Read shared answer state without starting a page-level subscription. */
export function useValidationQuestionsAnswers(householdId: string) {
  const answers = useGuardedContext(
    ValidationQuestionsAnswersContext,
    'ValidationQuestionsAnswersProvider'
  );
  if (answers.householdId !== householdId) {
    return { answeredAt: null, ready: false, unavailable: false };
  }
  return answers;
}
